import React from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Crown, Flame, BookKey, ArrowRight, Sparkles } from "lucide-react";
import { formatRp } from "@/lib/tiers";

export default function MembershipPage() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const tiers = [
    {
      key: "member", title: "Member", subtitle: "The Curious", price: "Free",
      icon: <BookKey className="w-5 h-5" />,
      perks: ["Browse all public TL releases", "Comment and join discussions", "Receive announcements"],
      theme: "border-white/10 bg-zinc-950",
      cta: { to: "/register", label: "Create Account", color: "bg-white/10 hover:bg-white/20" },
    },
    {
      key: "elite_cultist", title: "Elite Cultist", subtitle: "The Devoted", price: `${formatRp(settings.elite_price)} / ${settings.membership_days || 30} days`,
      icon: <Crown className="w-5 h-5" />,
      perks: ["Everything in Member", "Access Elite-only TL games", "Early access patches", "Priority support"],
      theme: "border-violet-500/40 bg-gradient-to-b from-violet-950/40 to-black glow-violet",
      cta: { to: "/checkout/elite_cultist", label: "Become Elite", color: "bg-violet-600 hover:bg-violet-500" },
    },
    {
      key: "hardcore_cultist", title: "Hardcore Cultist", subtitle: "The Initiated", price: `${formatRp(settings.hardcore_price)} / ${settings.membership_days || 30} days`,
      icon: <Flame className="w-5 h-5" />,
      perks: ["Everything in Elite", "Access ALL TL games", "Exclusive Hardcore releases", "Behind-the-scenes notes", "Direct line to translators"],
      theme: "border-rose-500/40 bg-gradient-to-b from-rose-950/40 to-black glow-crimson",
      cta: { to: "/checkout/hardcore_cultist", label: "Become Hardcore", color: "bg-rose-600 hover:bg-rose-500" },
      featured: true,
    },
    {
      key: "god_tier_supporter", title: "God Tier Supporter", subtitle: "The Patron",
      price: `${formatRp(settings.god_tier_price)} / ${settings.membership_days || 30} days`,
      icon: <Sparkles className="w-5 h-5" />,
      perks: [
        "Everything in Hardcore (full access)",
        "Animated God Tier badge across the site",
        "Listed in the Supporter Hall",
        "Eligible for Monthly & All-Time leaderboards",
        "Top 3 supporters earn animated Top 1/2/3 badges",
      ],
      theme: "border-amber-400/50 bg-gradient-to-b from-amber-950/30 via-rose-950/20 to-black shadow-[0_0_40px_rgba(251,191,36,0.25)]",
      cta: { to: "/checkout/god_tier_supporter", label: "Become a Patron", color: "bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-black" },
      crown: true,
    },
  ];

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <div className="font-mystic text-rose-400 text-sm tracking-widest">儀式 — RITES OF ENTRY</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white mt-3 tracking-tighter">Choose Your Devotion</h1>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto font-mystic italic">
            Four ranks, each unsealing more forbidden translations. Your tribute renews every {settings.membership_days || 30} days.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((t) => (
            <div key={t.key} className={`relative p-7 rounded-2xl border ${t.theme} transition`}>
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-rose-600 text-white text-[10px] font-bold tracking-widest shadow-[0_0_20px_rgba(225,29,72,0.55)]">
                  MOST POPULAR
                </div>
              )}
              {t.crown && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full badge-god text-[10px] tracking-widest">
                  GOD TIER
                </div>
              )}
              <div className="flex items-center gap-2 text-rose-300/90">
                {t.icon}
                <span className="text-xs uppercase tracking-widest font-mystic">{t.subtitle}</span>
              </div>
              <h2 className="font-display text-2xl font-bold text-white mt-3">{t.title}</h2>
              <div className="mt-1 font-display text-lg text-white/90">{t.price}</div>
              <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2"><Check className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />{p}</li>
                ))}
              </ul>
              <div className="mt-6">
                {user && user !== false && user.role === t.key ? (
                  <div className="text-center text-sm text-rose-300 font-semibold border border-rose-500/40 rounded-md py-3">Your current tier</div>
                ) : user && user !== false && user.role === "admin" ? (
                  <div className="text-center text-sm text-amber-300 font-semibold border border-amber-500/40 rounded-md py-3">Admin</div>
                ) : (
                  <Link to={user && user !== false ? t.cta.to : `/login?next=${encodeURIComponent(t.cta.to)}`} data-testid={`tier-cta-${t.key}`}>
                    <Button className={`w-full ${t.cta.color} py-6 font-semibold`}>
                      {t.cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 glass rounded-xl p-7">
          <h3 className="font-display text-xl font-bold text-white">How payment works</h3>
          <ol className="mt-3 text-sm text-zinc-300 space-y-1.5 list-decimal pl-5">
            <li>Select a tier and a payment method (QRIS / DANA / OVO / GoPay / Bank Transfer).</li>
            <li>Transfer the exact amount using the instructions shown.</li>
            <li>Upload your payment proof — we review manually within 24h.</li>
            <li>Your account is upgraded for {settings.membership_days || 30} days upon approval.</li>
          </ol>
        </div>
      </section>
    </PageShell>
  );
}
